/**
 * window 'error' + 'unhandledrejection' -> CrashReporter, rate limited (a
 * render-loop error must not flood the API). Listeners are added, not assigned,
 * so window.onerror set by others keeps working.
 */
import type { CrashReporter } from './types.js';

export interface GlobalHandlerOptions {
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  /** at most this many reports per window */
  maxPerWindow?: number;
  windowMs?: number;
  now?: () => number;
}

export class GlobalErrorHandlers {
  private sent: number[] = [];
  private installed = false;
  /** reports dropped by the rate limit since install */
  dropped = 0;

  constructor(
    private readonly reporter: CrashReporter,
    private readonly o: GlobalHandlerOptions = {},
  ) {}

  private get target(): Pick<Window, 'addEventListener' | 'removeEventListener'> {
    return this.o.target ?? window;
  }

  private readonly onError = (e: Event) => {
    const ev = e as ErrorEvent;
    if (!ev.error && !ev.message) return; // resource load errors: not crashes
    this.report(ev.error ?? ev.message, 'error', ev.filename ? { file: ev.filename, line: ev.lineno ?? 0, col: ev.colno ?? 0 } : undefined);
  };

  private readonly onRejection = (e: Event) => {
    this.report((e as PromiseRejectionEvent).reason, 'unhandledrejection');
  };

  install(): this {
    if (this.installed) return this;
    this.target.addEventListener('error', this.onError);
    this.target.addEventListener('unhandledrejection', this.onRejection);
    this.installed = true;
    return this;
  }

  uninstall(): void {
    if (!this.installed) return;
    this.target.removeEventListener('error', this.onError);
    this.target.removeEventListener('unhandledrejection', this.onRejection);
    this.installed = false;
  }

  private report(error: unknown, kind: 'error' | 'unhandledrejection', context?: Record<string, string | number>): void {
    const now = (this.o.now ?? Date.now)();
    const windowMs = this.o.windowMs ?? 60_000;
    this.sent = this.sent.filter((t) => now - t < windowMs);
    if (this.sent.length >= (this.o.maxPerWindow ?? 10)) {
      this.dropped += 1;
      return;
    }
    this.sent.push(now);
    this.reporter.capture(error, kind, context);
  }
}
